import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MagicLinkConsume from "./pages/MagicLinkConsume";
import Dashboard from "./pages/Dashboard";
import Trades from "./pages/Trades";
import TradeForm from "./pages/TradeForm";
import Settings from "./pages/Settings";
import Coach from "./pages/Coach";
import TradeDoctor from "./pages/TradeDoctor";
import DeltaConnect from "./pages/DeltaConnect";
import UpgradeGate from "./components/UpgradeGate";
import { Terms, Privacy } from "./pages/Legal";
import AdminHealth from "./pages/AdminHealth";
import AdminQuickUpgrade from "./pages/AdminQuickUpgrade";
import AdminRotatePassword from "./pages/AdminRotatePassword";
import AdminDeactivateUser from "./pages/AdminDeactivateUser";
import AdminUserLookup from "./pages/AdminUserLookup";
import AdminSupport from "./pages/AdminSupport";
import Support from "./pages/Support";
import Pricing from "./pages/Pricing";

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  return user ? <Navigate to="/dashboard" replace /> : children;
}

// Iter 19 — Toaster styled per theme so it visually disappears on the page
// canvas instead of showing a hardcoded dark card on light mode.
function ThemedToaster() {
  const { theme } = useTheme();
  const isLight = theme === "light";
  return (
    <Toaster
      theme={isLight ? "light" : "dark"}
      position="top-right"
      toastOptions={{
        style: isLight ? {
          background: "#ffffff",
          border: "1px solid rgba(15,23,42,0.08)",
          boxShadow: "0 4px 16px rgba(15,23,42,0.10)",
          color: "#0f172a",
        } : {
          background: "#121214",
          border: "1px solid #27272A",
          color: "#F8FAFC",
        },
      }}
    />
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      {/* iter130 Phase A — password-reset + magic-link entry points.
          Deliberately NOT wrapped in GuestOnly: a logged-in user
          following an emailed link should still be able to complete
          the flow (e.g. resetting a password after a lost device). */}
      <Route path="/auth/forgot" element={<ForgotPassword />} />
      <Route path="/auth/reset" element={<ResetPassword />} />
      <Route path="/auth/magic" element={<MagicLinkConsume />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/trades" element={<Trades />} />
        <Route path="/trades/new" element={<TradeForm />} />
        <Route path="/trades/:id/edit" element={<TradeForm />} />
        <Route path="/trade-doctor" element={<UpgradeGate feature="trade_doctor"><TradeDoctor /></UpgradeGate>} />
        <Route path="/trade-doctor/connect-delta" element={<UpgradeGate feature="delta_sync"><DeltaConnect /></UpgradeGate>} />
        <Route path="/admin/health" element={<AdminHealth />} />
        <Route path="/admin/quick-upgrade" element={<AdminQuickUpgrade />} />
        <Route path="/admin/rotate-password" element={<AdminRotatePassword />} />
        <Route path="/admin/deactivate-user" element={<AdminDeactivateUser />} />
        <Route path="/admin/users" element={<AdminUserLookup />} />
        <Route path="/admin/support" element={<AdminSupport />} />
        <Route path="/support" element={<Support />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/coach" element={<Coach />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
            <ThemeProvider>
              <LanguageProvider>
                <AppRoutes />
                <ThemedToaster />
              </LanguageProvider>
            </ThemeProvider>
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
